// frontend/src/components/settings/PlanningDefaultsFields.jsx
//
// The five store-level planning defaults as form controls, in three pieces a
// screen places where it wants them: the inventory target row (mode, and the
// days of supply or what safety stock is), the lead time, and the order
// cadence row (review frequency and, for the anchored cadences, the preferred
// order day). Store settings' Planning card and the install setup step render
// them over the same form shape (`planningDefaults.js`), so the two screens
// cannot say different things about the same fields.
//
// Each piece takes `form`, the five fields — the two typed ones as the digits
// the control shows; `onChange(patch)`, which receives the fields an edit
// moves — the cadence pair moves as ONE patch, because the endpoint validates
// it together and a frequency change that left the old day behind would be a
// 400 naming a control the merchant did not touch (`orderDayForCadence` is
// the rule's one home); and `errors`, the endpoint's messages by bare field
// name, each shown on its control until the merchant edits that field.

import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormHelperText, InputAdornment, MenuItem, TextField } from '@mui/material';

import {
  ORDER_DAYS, REVIEW_FREQUENCIES, orderDayForCadence, requiresPreferredOrderDay,
} from '../../constants/planning';
import {
  INVENTORY_TARGET_MODES, TARGET_DAYS_OF_SUPPLY_MAX, TARGET_DAYS_OF_SUPPLY_MIN,
  isValidTargetDaysOfSupply, readsDaysOfSupply,
} from '../../utils/inventoryTargetMode';
import { LEAD_TIME_DAYS_MAX, LEAD_TIME_DAYS_MIN, isValidLeadTimeDays } from '../../utils/leadTime';
import { orderDayLabel } from '../../pages/supplierSchedule';
import './PlanningDefaultsFields.css';

// Days of supply and lead time are whole numbers of days; each control holds
// the digits the merchant has typed so far. Non-digits are dropped on the way
// in rather than parsed later — `parseInt('12x')` is 12, which would save a
// number nobody typed.
const digits = (e) => e.target.value.replace(/\D/g, '');

// One message per field: `message` is what the endpoint said about the value
// it refused, else what the control can already see is wrong; without one the
// field shows its standing hint, if it has one.
const fieldMessage = (message, hint) => (
  message ? { error: true, helperText: message } : { helperText: hint }
);

/**
 * The inventory target mode, and beside it what that mode reads: the days of
 * supply as one sentence with the number inside it, or what safety stock is.
 * Under Safety stock the days control is absent, not muted: its value is never
 * sent there (`planningDefaults.js`), so there is nothing to set.
 */
export function InventoryTargetFields({ form, onChange, errors = {} }) {
  const { t } = useTranslation();
  const daysMessageId = useId();
  const daysValid = isValidTargetDaysOfSupply(String(form.target_days_of_supply));
  const daysMessage = errors.target_days_of_supply ?? (daysValid ? null : t(
    'storeSettings.targetDaysOfSupplyInvalid',
    { min: TARGET_DAYS_OF_SUPPLY_MIN, max: TARGET_DAYS_OF_SUPPLY_MAX },
  ));

  return (
    <div className="planning-row">
      <TextField
        select
        label={t('storeSettings.inventoryTargetMode')}
        value={form.inventory_target_mode}
        onChange={(e) => onChange({ inventory_target_mode: e.target.value })}
        size="small"
        fullWidth
        {...fieldMessage(errors.inventory_target_mode)}
      >
        {INVENTORY_TARGET_MODES.map((mode) => (
          <MenuItem key={mode} value={mode}>
            {t(`storeSettings.inventoryTargetMode_${mode}`)}
          </MenuItem>
        ))}
      </TextField>
      {readsDaysOfSupply(form.inventory_target_mode) ? (
        <div>
          <div className="planning-days-line">
            <Trans
              i18nKey="storeSettings.keepDaysOfSupply"
              components={{
                days: (
                  <TextField
                    value={form.target_days_of_supply}
                    onChange={(e) => onChange({ target_days_of_supply: digits(e) })}
                    size="small"
                    error={daysMessage !== null}
                    className="planning-days-input"
                    slotProps={{
                      htmlInput: {
                        inputMode: 'numeric',
                        'aria-label': t('storeSettings.targetDaysOfSupply'),
                        'aria-describedby': daysMessage === null ? undefined : daysMessageId,
                      },
                    }}
                  />
                ),
              }}
            />
          </div>
          {daysMessage !== null && (
            <FormHelperText id={daysMessageId} error>{daysMessage}</FormHelperText>
          )}
        </div>
      ) : (
        <p className="planning-safety-stock-note">{t('storeSettings.safetyStockExplained')}</p>
      )}
    </div>
  );
}

/**
 * The root of the Store → Supplier → Supplier catalog item lead-time chain
 * (utils/leadTime.js): a supplier with no lead time of its own reads this.
 */
export function LeadTimeField({ form, onChange, errors = {} }) {
  const { t } = useTranslation();
  const valid = isValidLeadTimeDays(String(form.lead_time_days));
  return (
    <TextField
      label={t('storeSettings.leadTimeDays')}
      value={form.lead_time_days}
      onChange={(e) => onChange({ lead_time_days: digits(e) })}
      size="small"
      fullWidth
      slotProps={{
        htmlInput: { inputMode: 'numeric', className: 'planning-number-input' },
        input: {
          endAdornment: <InputAdornment position="end">{t('common.days')}</InputAdornment>,
        },
      }}
      {...fieldMessage(
        errors.lead_time_days ?? (valid ? null : t('storeSettings.leadTimeDaysInvalid', {
          min: LEAD_TIME_DAYS_MIN, max: LEAD_TIME_DAYS_MAX,
        })),
        t('storeSettings.leadTimeDaysHint'),
      )}
    />
  );
}

/**
 * Review frequency and preferred order day. The day is absent rather than
 * disabled under Daily, which has no anchor: the endpoint rejects a day sent
 * with DAILY, so there must be no control offering one. Its half of the row
 * stays empty, so nothing beside it moves.
 */
export function OrderCadenceFields({ form, onChange, errors = {} }) {
  const { t } = useTranslation();

  const handleReviewFrequencyChange = (e) => {
    const reviewFrequency = e.target.value;
    onChange({
      review_frequency: reviewFrequency,
      preferred_order_day: orderDayForCadence(reviewFrequency, form.preferred_order_day),
    });
  };

  return (
    <div className="planning-row">
      <TextField
        select
        label={t('storeSettings.reviewFrequency')}
        value={form.review_frequency}
        onChange={handleReviewFrequencyChange}
        size="small"
        fullWidth
        {...fieldMessage(errors.review_frequency, t('storeSettings.reviewFrequencyHint'))}
      >
        {REVIEW_FREQUENCIES.map((frequency) => (
          <MenuItem key={frequency} value={frequency}>
            {t(`storeSettings.reviewFrequency_${frequency}`)}
          </MenuItem>
        ))}
      </TextField>
      {requiresPreferredOrderDay(form.review_frequency) && (
        <TextField
          select
          label={t('storeSettings.preferredOrderDay')}
          value={form.preferred_order_day}
          onChange={(e) => onChange({ preferred_order_day: Number(e.target.value) })}
          size="small"
          fullWidth
          {...fieldMessage(errors.preferred_order_day)}
        >
          {ORDER_DAYS.map((day) => (
            <MenuItem key={day} value={day}>
              {orderDayLabel(t, day, form.review_frequency)}
            </MenuItem>
          ))}
        </TextField>
      )}
    </div>
  );
}
